// „Mietkonto“ (Screen mieter/06) – nur gespeicherte Soll-Daten: monatliche Miete und Zusammensetzung,
// Fälligkeit, Zahlungsweise, Kaution. Kontostand, Buchungen und Überweisungsdaten fehlen, bis es die
// Buchhaltung gibt (Entscheidung 22.10.2026: ohne Daten ausblenden).
import { CalendarCheck, Vault, Wallet } from '@phosphor-icons/react';
import { GlasWidget, SeitenTitel } from '../../components/portal/bausteine';
import { euro, naechsteFaelligkeit, warmmiete, type MieterKontext } from '../../lib/portal/mieter';
import { kautionText, mietTeile, zahlweiseText } from '../../lib/portal/mieterE5';

const TEIL_FARBEN = ['var(--p-gold-text)', 'var(--p-blau-link)', 'var(--p-ton-warn-fg)', 'var(--p-lila-link)'];

export default function MieterMietkonto({ kontext, jetzt }: { kontext: MieterKontext; jetzt: Date }) {
  const m = kontext.mieter;
  const heute = jetzt.toISOString().slice(0, 10);
  const miete = warmmiete(m, heute);
  const teile = mietTeile(m, heute);
  const summe = teile.reduce((s, t) => s + t.betrag, 0) || 1;
  const faellig = naechsteFaelligkeit(m.miete_faellig_tag, jetzt);
  const zahlweise = zahlweiseText(m.zahlungsweise ?? null);
  const kaution = kautionText(m.kaution, m.kaution_ist ?? null, m.kaution_art ?? null);

  return (
    <div className="p-rein" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <SeitenTitel oben="Miete, Zahlungen und Kaution" titel="Mietkonto" />
      <div style={{ display: 'grid', gap: 14, alignItems: 'stretch', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))' }}>
        <GlasWidget farbe="konto" icon={Wallet} titel="Ihre monatliche Miete">
          <span className="p-zahl" style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em' }}>{euro(miete)}</span>
          {teile.length > 1 && (
            <div style={{ display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden', gap: 2 }} aria-hidden>
              {teile.map((t, i) => <span key={t.label} style={{ flex: t.betrag / summe, background: TEIL_FARBEN[i % TEIL_FARBEN.length] }} />)}
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {teile.map((t, i) => (
              <div key={t.label} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700 }}>
                <span aria-hidden style={{ width: 10, height: 10, borderRadius: 3, flexShrink: 0, background: TEIL_FARBEN[i % TEIL_FARBEN.length] }} />
                <span style={{ flex: 1, color: 'var(--p-w70)' }}>{t.label}</span>
                <span className="p-zahl">{euro(t.betrag)}</span>
              </div>
            ))}
          </div>
        </GlasWidget>
        <GlasWidget farbe="schluessel" icon={CalendarCheck} titel="Nächste Miete">
          <span className="p-zahl" style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em' }}>{euro(miete)}</span>
          <span style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.45, color: 'var(--p-w60)' }}>
            fällig am {faellig.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}{zahlweise ? `, ${zahlweise}` : ''}.
          </span>
        </GlasWidget>
        {Number(m.kaution) > 0 && (
          <GlasWidget farbe="wohnung" icon={Vault} titel="Mietkaution">
            <span className="p-zahl" style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em' }}>{euro(Number(m.kaution))}</span>
            {kaution && <span style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.45, color: 'var(--p-w60)' }}>{kaution}</span>}
          </GlasWidget>
        )}
      </div>
    </div>
  );
}
